import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ContextualHelp, Heading, Content, Text, ProgressCircle } from '@adobe/react-spectrum';
import Link from '@spectrum-icons/workflow/Link';
import Close from '@spectrum-icons/workflow/Close';
import { importDeckList } from '../../data/redux/slices/importSlice';
import { toast } from 'react-toastify';
import { getAnalytics, logEvent } from 'firebase/analytics';
import './SubmitForm.css';

const SUPPORTED_SITES = [
    { host: 'moxfield.com/', label: 'moxfield.com' },
    { host: 'archidekt.com/', label: 'archidekt.com' },
    { host: 'tappedout.net/', label: 'tappedout.net' },
    { host: 'manabox.app/', label: 'manabox.app' },
    // { host: 'mtggoldfish.com/', label: 'mtggoldfish.com' },
    { host: 'scryfall.com/', label: 'scryfall.com' },
    { host: 'topdecked.com/', label: 'topdecked.com' },
    { host: 'topdeck.gg/', label: 'topdeck.gg' },
    { host: 'hareruyamtg.com/', label: 'hareruyamtg.com' },
];

export function SubmitForm(props) {
    const dispatch = useDispatch();
    const [url, setUrl] = useState('');
    const isImporting = useSelector((state) => state?.import?.isImporting);

    const urlCleanedValue = url ? url.trim() : '';
    const isDisabled = !urlCleanedValue || isImporting;

    const handleSubmit = () => {
        if (!urlCleanedValue) return;

        if (/archidekt\.com\/folders/.test(urlCleanedValue)) {
            toast('Folder URIs not supported');
            return;
        }
        if (/archidekt\.com\/compare/.test(urlCleanedValue)) {
            toast('Compare URIs not supported');
            return;
        }
        if (/archidekt\.com\/articles/.test(urlCleanedValue)) {
            toast('Sandbox URIs not supported');
            return;
        }
        if (/archidekt\.com\/snapshots/.test(urlCleanedValue)) {
            toast('Snapshot URIs not supported');
            return;
        }
        if (/archidekt\.com\/sandbox/.test(urlCleanedValue)) {
            toast('Sandbox URIs not supported');
            return;
        }
        if (/archidekt\.com\/search/.test(urlCleanedValue)) {
            toast('Search URIs not supported');
            return;
        }
        // if (/mtggoldfish\.com\/articles/.test(urlCleanedValue)) {
        //     toast('MTGGoldfish articles not supported');
        //     return;
        // }
        if (/\/playtest/.test(urlCleanedValue)) {
            toast('Playtest URIs not supported');
            return;
        }

        const hostHit = SUPPORTED_SITES.some(({ host }) => urlCleanedValue.includes(host));
        if (!hostHit) {
            toast(
                `Sorry, we currently only accept deck lists from ${SUPPORTED_SITES.slice(0, -1)
                    .map((s) => s.label)
                    .join(', ')}, and ${SUPPORTED_SITES[SUPPORTED_SITES.length - 1].label}.`
            );
            return;
        }

        dispatch(importDeckList(urlCleanedValue));
        try {
            logEvent(getAnalytics(), 'import_start', { url: urlCleanedValue });
        } catch (_err) {
            // analytics is best-effort; never block the import on telemetry
        }
        props?.onStartCallback && props.onStartCallback();
    };

    const handleKeyDown = (evt) => {
        if (evt.key === 'Enter') {
            evt.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div className={`submitForm${isImporting ? ' submitForm--busy' : ''}`}>
            <div className="submitFormInputWrap">
                <span className="submitFormInputIcon" aria-hidden="true">
                    <Link size="S" />
                </span>
                <input
                    type="url"
                    inputMode="url"
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                    className="submitFormInput"
                    placeholder="https://www.moxfield.com/decks/…"
                    value={url}
                    onChange={(evt) => setUrl(evt.target.value)}
                    onFocus={(evt) => evt.target.select()}
                    onKeyDown={handleKeyDown}
                    aria-label="Decklist URL"
                    disabled={isImporting}
                />
                {url && !isImporting && (
                    <button
                        type="button"
                        className="submitFormClear"
                        onClick={() => setUrl('')}
                        aria-label="Clear URL"
                        title="Clear"
                    >
                        <Close size="XS" />
                    </button>
                )}
                <span className="submitFormHelpSlot">
                    <ContextualHelp variant="info" UNSAFE_className="submitFormHelp">
                        <Heading>What can I import?</Heading>
                        <Content>
                            <Text>We accept Commander-legal decks hosted on:</Text>
                            <ul className="submitFormHelpList">
                                {SUPPORTED_SITES.map(({ label }) => (
                                    <li key={label}>{label}</li>
                                ))}
                            </ul>
                        </Content>
                    </ContextualHelp>
                </span>
            </div>

            <button
                type="button"
                className="submitFormSubmit"
                onClick={handleSubmit}
                disabled={isDisabled}
                aria-label="Submit deck for analysis"
            >
                <span className="submitFormSubmitShaker" aria-hidden="true" />
                <span className="submitFormSubmitLabel">{isImporting ? 'Seasoning…' : 'Get salty!'}</span>
                {isImporting && (
                    <ProgressCircle
                        size="S"
                        isIndeterminate
                        aria-label="Importing"
                        UNSAFE_className="submitFormSubmitSpinner"
                    />
                )}
            </button>
        </div>
    );
}
